/**
 * CommandCenterTab — Phase 5 operational landing for Central Admin.
 *
 * Lives at /admin/dashboard ( tab="dashboard" ). Top section is the
 * live operational view (status strip + Mali map + urgency queue),
 * historical analytics collapses below.
 *
 * No state mutation of alerts happens here — clicking an alert
 * navigates to the Alerts tab pre-focused on the row, which keeps the
 * dispatch workflow as the single source of truth.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import maplibregl from "maplibre-gl";
import "maplibre-gl/dist/maplibre-gl.css";
import { getStyle, installBuildingExtrusion, applySatelliteZoomCap } from "../../lib/mapStyles";
import { getCountryBoundary, installCountryMask } from "../../lib/countryMask";
import { adminCommandCenterSummary } from "../../lib/api";
import { ccBus } from "../../lib/commandCenterBus";
import { EMERGENCY_META } from "../EmergencyTypeGrid";
import { EMERGENCY_FR } from "../../i18n.jsx";
import { AnalyticsSummary } from "../AnalyticsSummary";
import { TimeFilterBar } from "./TimeFilterBar";
import { getCountryConfig } from "../../lib/countryConfig";
import {
  AlertTriangle, Clock, Crosshair, Loader2, Layers, Maximize2, Minimize2,
  MapPin, Paperclip, RadioTower, RotateCw, Siren, Target, WifiOff,
} from "lucide-react";

// ── Constants ────────────────────────────────────────────────────────
// Initial view centred on the active country (read from country_config
// so a redeployment for e.g. SOS Guinée recentres without a code change).
const _ccMap = (getCountryConfig() || {}).map || {};
const _ccName = (getCountryConfig() || {}).name || "Mali";
const MALI_CENTER = {
  lng: typeof _ccMap.center_lng === "number" ? _ccMap.center_lng : -3.5,
  lat: typeof _ccMap.center_lat === "number" ? _ccMap.center_lat : 17.5,
};
const MALI_VIEW = {
  center: [MALI_CENTER.lng, MALI_CENTER.lat],
  zoom: typeof _ccMap.initial_zoom === "number" ? _ccMap.initial_zoom : 4.6,
};
const REFRESH_MS = 30_000;

const WINDOW_OPTIONS = [
  { value: "24h", label: "24h" },
  { value: "today", label: "Aujourd'hui" },
  { value: "7d", label: "7j" },
  { value: "30d", label: "30j" },
];

const STATUS_LABEL = {
  received: "Nouveau",
  under_review: "Examen",
  in_process: "En cours",
  responder_assigned: "Équipe assignée",
  suspect: "Suspect",
};
const STATUS_TONE = {
  received: "text-rose-300 border-rose-500/40 bg-rose-500/10",
  under_review: "text-amber-300 border-amber-500/40 bg-amber-500/10",
  in_process: "text-cyan-300 border-cyan-500/40 bg-cyan-500/10",
  responder_assigned: "text-emerald-300 border-emerald-500/40 bg-emerald-500/10",
  suspect: "text-zinc-300 border-zinc-500/40 bg-zinc-500/10",
};

const fmtElapsed = (min) => {
  if (min == null) return "—";
  if (min < 1) return "à l'instant";
  if (min < 60) return `${min} min`;
  const h = Math.floor(min / 60);
  const m = min % 60;
  if (h < 24) return m ? `${h}h${String(m).padStart(2, "0")}` : `${h}h`;
  const d = Math.floor(h / 24);
  return `${d}j ${h % 24}h`;
};

const StripCell = ({ label, value, tone = "neutral", sub, testid, pulseClass = "" }) => {
  const TONE = {
    neutral: "border-zinc-800 text-zinc-100",
    warn: "border-amber-500/50 text-amber-200",
    danger: "border-rose-500/60 text-rose-200 bg-rose-500/[0.07]",
    success: "border-emerald-500/40 text-emerald-200",
    info: "border-cyan-500/40 text-cyan-200",
  }[tone] || "border-zinc-800 text-zinc-100";
  return (
    <div
      data-testid={testid}
      className={`shrink-0 border ${TONE} bg-[#0b0f14] rounded px-3 py-2 min-w-[110px] ${pulseClass}`}
    >
      <div className="text-[10px] font-semibold uppercase tracking-[0.12em] text-zinc-500">{label}</div>
      <div className="font-display text-2xl font-bold tracking-tight tabular-nums leading-none mt-1">
        {value}
      </div>
      {sub && (
        <div className="text-[10px] text-zinc-500 mt-1 font-mono truncate" title={sub}>
          {sub}
        </div>
      )}
    </div>
  );
};

const QueueRow = ({ a, onClick, isNew = false }) => {
  const meta = EMERGENCY_META[a.emergency_type] || {};
  const label = EMERGENCY_FR[a.emergency_type]?.label || a.emergency_type;
  const dotColor = meta.color || "#64748B";
  const statusKey = a.internal_status || "received";
  const statusLabel = STATUS_LABEL[statusKey] || statusKey;
  const statusTone = STATUS_TONE[statusKey] || STATUS_TONE.suspect;
  const overdue = (a.elapsed_minutes || 0) > 30 && a.is_unassigned;
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={`cc-queue-row-${a.id}`}
      className={`w-full text-left border bg-[#0b0f14] hover:border-zinc-600 transition-colors px-3 py-2.5 rounded ${
        a.is_critical
          ? "border-rose-500/60 shadow-[inset_0_0_0_1px_rgba(244,63,94,0.12)]"
          : overdue
            ? "border-amber-500/50"
            : "border-zinc-800"
      } ${isNew ? "cc-slide-in" : ""}`}
    >
      <div className="flex items-start gap-2">
        <span
          aria-hidden
          className="mt-1 w-2.5 h-2.5 rounded-full shrink-0"
          style={{ background: dotColor, boxShadow: `0 0 8px ${dotColor}` }}
        />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <div
              className="font-display text-sm font-black tracking-tight truncate"
              style={{ color: dotColor }}
            >
              {label}
            </div>
            {a.is_sensitive && (
              <span className="text-[9px] font-bold uppercase tracking-widest text-rose-300 border border-rose-500/50 px-1.5 py-0.5 rounded">
                Sensible
              </span>
            )}
            {a.is_unassigned ? (
              <span className="ml-auto text-[9px] font-bold uppercase tracking-widest text-amber-300 border border-amber-500/40 bg-amber-500/10 px-1.5 py-0.5 rounded">
                Non-assignée
              </span>
            ) : (
              <span className="ml-auto text-[9px] font-bold uppercase tracking-widest text-emerald-300 border border-emerald-500/40 bg-emerald-500/10 px-1.5 py-0.5 rounded">
                Dispatchée
              </span>
            )}
          </div>
          <div className="flex items-center gap-3 mt-1 text-[10px] text-zinc-400 font-mono">
            <span className="inline-flex items-center gap-1">
              <Clock className="w-3 h-3" /> {fmtElapsed(a.elapsed_minutes)}
            </span>
            {a.coordinates && (
              <span className="inline-flex items-center gap-1 truncate">
                <MapPin className="w-3 h-3" />
                {a.coordinates.latitude?.toFixed(3)}, {a.coordinates.longitude?.toFixed(3)}
              </span>
            )}
            {a.attachment_count > 0 && (
              <span className="inline-flex items-center gap-1 text-violet-300">
                <Paperclip className="w-3 h-3" /> {a.attachment_count}
              </span>
            )}
            <span className={`ml-auto text-[9px] font-bold uppercase tracking-widest border px-1.5 py-0.5 rounded ${statusTone}`}>
              {statusLabel}
            </span>
          </div>
          {a.description && (
            <p className="text-[11px] text-zinc-400 mt-1 line-clamp-2">{a.description}</p>
          )}
        </div>
      </div>
    </button>
  );
};

export const CommandCenterTab = () => {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [timeWindow, setTimeWindow] = useState("24h");
  // Iter80 — support custom since/until when the operator picks the
  // "Période personnalisée" preset. Passed straight through to the
  // command-center summary endpoint (which already accepts them).
  const [tfSince, setTfSince] = useState("");
  const [tfUntil, setTfUntil] = useState("");
  const [mapMode, setMapMode] = useState("markers"); // "markers" | "heatmap"
  // Cartography layer — parity with Carte tab. Defaults to vector
  // ("Plan / Liberty") so the dashboard stays text-friendly. The
  // satellite layer is wired for the same "what's that building"
  // moments operators rely on in Carte.
  const [mapLayer, setMapLayer] = useState("vector"); // "vector" | "hybrid" | "dark"
  const [isFullscreen, setIsFullscreen] = useState(false);
  // Map style version key — bumped on every setStyle() so the marker /
  // heatmap re-binding effect re-runs cleanly without leaking layers.
  const [styleVersion, setStyleVersion] = useState(0);
  // Live state — driven by the admin firehose bus (see useAdminFirehose).
  // Seed from the bus's last cached value so navigating away and back to
  // Tableau doesn't flicker "Hors-ligne" while the still-open WS waits
  // for its next open/close event to fire (which can be minutes away).
  const [live, setLive] = useState(() => !!ccBus.last("ws.live")?.live);
  // Newly-arrived alert ids (within the last few seconds) — used to
  // trigger the slide-in animation on QueueRow exactly once.
  const [newIds, setNewIds] = useState(() => new Set());
  // Strip-pulse triggers: bumped each time a relevant WS event lands.
  // Keyed counters force React to re-mount the CSS animation reliably.
  const [criticalPulseKey, setCriticalPulseKey] = useState(0);
  const [unassignedPulseKey, setUnassignedPulseKey] = useState(0);
  const [activePulseKey, setActivePulseKey] = useState(0);
  const mapContainerRef = useRef(null);
  const mapCardRef = useRef(null);
  const mapRef = useRef(null);
  const markersRef = useRef([]);
  const lastFetchRef = useRef(0);
  // Coalesced refresh debounce so a 5-event burst doesn't fire 5 GETs.
  const refreshDebounceRef = useRef(null);

  const fetchSummary = useCallback(async ({ silent = false } = {}) => {
    const stamp = ++lastFetchRef.current;
    if (!silent) setLoading(true);
    else setRefreshing(true);
    try {
      // Iter80 — pass custom since/until when preset === "custom" so
      // the Tableau summary respects operator-picked ranges.
      const params = { window: timeWindow, queue_limit: 30 };
      if (timeWindow === "custom") {
        if (tfSince) params.since = new Date(tfSince).toISOString();
        if (tfUntil) params.until = new Date(tfUntil).toISOString();
      }
      const d = await adminCommandCenterSummary(params);
      if (lastFetchRef.current !== stamp) return;
      setData(d);
    } catch (e) {
      // Toast intentionally avoided on auto-refresh; the user can hit
      // refresh manually if needed.
      console.warn("command-center summary failed:", e?.response?.data || e?.message);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [timeWindow, tfSince, tfUntil]);

  // Initial + window-change fetch
  useEffect(() => { fetchSummary(); }, [fetchSummary]);

  // Background refresh every REFRESH_MS while tab is mounted.
  // Keeps polling alive as a FALLBACK when the WebSocket disconnects.
  useEffect(() => {
    const id = setInterval(() => fetchSummary({ silent: true }), REFRESH_MS);
    return () => clearInterval(id);
  }, [fetchSummary]);

  // ── Live WebSocket bridge ─────────────────────────────────────────
  // Subscribes to the existing admin firehose (opened once by
  // useAdminFirehose at the AdminPanelPage level — we never own the
  // socket, we only listen to relay events). All events trigger a
  // coalesced silent refresh so the strip + queue + map markers stay
  // authoritative; alert.created additionally drives an optimistic
  // prepend + pulse animations for instant feedback (<100 ms).
  useEffect(() => {
    const queueRefresh = () => {
      if (refreshDebounceRef.current) clearTimeout(refreshDebounceRef.current);
      refreshDebounceRef.current = setTimeout(() => {
        fetchSummary({ silent: true });
      }, 350);
    };

    const onCreated = (msg) => {
      const a = msg?.alert;
      if (!a || !a.id) return;
      // Compute the same operational flags the backend would so the
      // optimistic row matches the eventual server-truth row.
      const isSensitive = !!a.is_sensitive;
      const isUnassigned = !(a.current_assigned_agence_ids || []).length;
      const isCritical = isSensitive || isUnassigned; // brand-new alert: ≤30min so only sensitive flips critical
      setData((prev) => {
        if (!prev) return prev;
        if ((prev.queue || []).some((q) => q.id === a.id)) return prev;
        const optimistic = {
          ...a,
          elapsed_minutes: 0,
          is_unassigned: isUnassigned,
          is_critical: isCritical,
          is_sensitive: isSensitive,
          urgency_score: 9999, // top of the queue until refresh
          attachment_count: (a.attachments || []).length,
        };
        return {
          ...prev,
          metrics: {
            ...prev.metrics,
            active: (prev.metrics?.active || 0) + 1,
            unassigned: (prev.metrics?.unassigned || 0) + (isUnassigned ? 1 : 0),
            critical: (prev.metrics?.critical || 0) + (isCritical ? 1 : 0),
          },
          queue: [optimistic, ...(prev.queue || [])].slice(0, 50),
        };
      });
      setNewIds((prev) => {
        const next = new Set(prev);
        next.add(a.id);
        return next;
      });
      // Clear the slide-in flag after the animation has finished so
      // subsequent re-renders don't re-trigger it.
      setTimeout(() => {
        setNewIds((prev) => {
          if (!prev.has(a.id)) return prev;
          const next = new Set(prev);
          next.delete(a.id);
          return next;
        });
      }, 1600);
      setActivePulseKey((k) => k + 1);
      if (isCritical) setCriticalPulseKey((k) => k + 1);
      if (isUnassigned) setUnassignedPulseKey((k) => k + 1);
      queueRefresh();
    };

    const onChange = () => queueRefresh();
    const onLive = (msg) => setLive(!!msg?.live);

    const unsubs = [
      ccBus.on("ws.live", onLive),
      ccBus.on("alert.created", onCreated),
      ccBus.on("alert.acknowledged", onChange),
      ccBus.on("alert.status_changed", onChange),
      ccBus.on("alert.dispatched", onChange),
      ccBus.on("alert.reassigned", onChange),
      ccBus.on("alert.review", onChange),
      ccBus.on("alert.operational", onChange),
      // Iter90 — GPS audit: a post-SOS location refinement landed on an
      // already-visible alert; refetch so the marker moves to the
      // improved fix without the dispatcher refreshing the page.
      ccBus.on("alert.location_updated", onChange),
    ];
    return () => {
      unsubs.forEach((u) => u && u());
      if (refreshDebounceRef.current) clearTimeout(refreshDebounceRef.current);
    };
  }, [fetchSummary]);

  // ── Map lifecycle ─────────────────────────────────────────────────
  useEffect(() => {
    if (!mapContainerRef.current || mapRef.current) return;
    const map = new maplibregl.Map({
      container: mapContainerRef.current,
      style: getStyle(mapLayer === "vector" ? "liberty" : mapLayer),
      center: MALI_VIEW.center,
      zoom: MALI_VIEW.zoom,
      pitch: 0,
      bearing: 0,
      attributionControl: false,
      // Parity with Carte tab — tilt + rotation are the cornerstone of
      // operator situational awareness, especially in city-scale zoom.
      pitchWithRotate: true,
      dragRotate: true,
      touchZoomRotate: true,
      doubleClickZoom: true,
      maxPitch: 60,
    });
    // Iter85 — Native-resolution cap for Esri Satellite (see mapStyles.js).
    applySatelliteZoomCap(map, mapLayer);
    map.addControl(new maplibregl.AttributionControl({ compact: true }), "bottom-right");
    // Iter78 — Parity with Users Map: move tilt/compass control to
    // bottom-left so all map-mode-adjacent controls (tilt + compass +
    // scale) live in the same corner and never clash with the top-right
    // fullscreen button. `ScaleControl` still lives at bottom-left; the
    // MapLibre control container stacks them vertically automatically.
    map.addControl(new maplibregl.ScaleControl({ unit: "metric", maxWidth: 120 }), "bottom-left");
    map.addControl(new maplibregl.NavigationControl({ showCompass: true, visualizePitch: true }), "bottom-left");
    // Built-in fullscreen — works even when the user hits F11; the
    // explicit toolbar button still calls our own toggle so React state
    // (and the toolbar icon) stays in sync.
    const fsControl = new maplibregl.FullscreenControl();
    map.addControl(fsControl, "top-right");
    mapRef.current = map;
    try {
      if (localStorage.getItem("rr_poi_debug") === "1") window.__rrTableauMap = map;
    } catch (_e) { /* noop */ }
    const ro = new ResizeObserver(() => {
      try { map.resize(); } catch { /* noop */ }
    });
    ro.observe(mapContainerRef.current);
    map.on("load", () => {
      getCountryBoundary().then((boundary) => {
        installCountryMask(map, boundary);
        installBuildingExtrusion(map, mapLayer);
        setStyleVersion((v) => v + 1);
      });
    });
    return () => {
      ro.disconnect();
      map.remove();
      mapRef.current = null;
      markersRef.current = [];
    };
  }, []);

  // Swap base style when the operator changes layer. Triggers a
  // styledata event after which we re-install custom sources/layers.
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    const styleKey = mapLayer === "vector" ? "liberty" : mapLayer;
    try {
      map.setStyle(getStyle(styleKey));
      applySatelliteZoomCap(map, mapLayer);
    } catch (e) {
      console.warn("[CommandCenter] setStyle failed:", e?.message || e);
      return;
    }
    const reinstall = () => {
      getCountryBoundary().then((boundary) => {
        installCountryMask(map, boundary);
        installBuildingExtrusion(map, mapLayer);
        // Bump the version so the markers+heatmap effect re-binds onto
        // the fresh style instance.
        setStyleVersion((v) => v + 1);
      });
    };
    map.once("styledata", reinstall);
    return () => { try { map.off("styledata", reinstall); } catch { /* noop */ } };
  }, [mapLayer]);

  // Sync markers / heatmap source when data, mapMode, or the underlying
  // style changes. The styleVersion bump from setStyle() forces a clean
  // re-bind so we never reference layers that the new style nuked.
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !data) return;

    // Wipe previous markers.
    markersRef.current.forEach((m) => m.remove());
    markersRef.current = [];

    const points = (data.map_markers || []).filter(
      (p) => p.coordinates && p.coordinates.latitude && p.coordinates.longitude,
    );

    const ensureLoaded = (cb) => {
      if (map.isStyleLoaded()) cb();
      else map.once("load", cb);
    };

    ensureLoaded(() => {
      // Heatmap source / layer management.
      const SRC = "cc-active-alerts";
      const LYR_HEAT = "cc-active-heat";
      const fc = {
        type: "FeatureCollection",
        features: points.map((p) => ({
          type: "Feature",
          properties: {
            urgency: p.urgency_score || 1,
            critical: p.is_critical ? 1 : 0,
          },
          geometry: {
            type: "Point",
            coordinates: [p.coordinates.longitude, p.coordinates.latitude],
          },
        })),
      };
      if (!map.getSource(SRC)) {
        map.addSource(SRC, { type: "geojson", data: fc });
      } else {
        map.getSource(SRC).setData(fc);
      }
      if (mapMode === "heatmap") {
        if (!map.getLayer(LYR_HEAT)) {
          map.addLayer({
            id: LYR_HEAT,
            type: "heatmap",
            source: SRC,
            paint: {
              "heatmap-weight": ["interpolate", ["linear"], ["get", "urgency"], 0, 0.1, 200, 1],
              "heatmap-intensity": 1.2,
              "heatmap-radius": 28,
              "heatmap-opacity": 0.7,
              "heatmap-color": [
                "interpolate", ["linear"], ["heatmap-density"],
                0, "rgba(34, 211, 238, 0)",
                0.2, "rgba(34, 211, 238, 0.4)",
                0.5, "rgba(245, 158, 11, 0.7)",
                0.8, "rgba(244, 63, 94, 0.85)",
                1, "rgba(244, 63, 94, 1)",
              ],
            },
          });
        }
      } else if (map.getLayer(LYR_HEAT)) {
        map.removeLayer(LYR_HEAT);
      }

      // Markers only when not in heatmap mode (keep map readable).
      if (mapMode === "markers") {
        points.forEach((p) => {
          const meta = EMERGENCY_META[p.emergency_type] || {};
          const color = meta.color || "#64748B";
          const isNewMarker = newIds.has(p.id);
          // Outer wrapper carries the optional flashing ring so the dot
          // itself keeps the steady pulse for criticals — the two
          // animations don't fight.
          const wrap = document.createElement("div");
          wrap.setAttribute("data-testid", `cc-map-marker-${p.id}`);
          wrap.style.position = "relative";
          wrap.style.width = "0";
          wrap.style.height = "0";
          if (isNewMarker) {
            const ring = document.createElement("div");
            ring.className = "cc-marker-flash";
            ring.style.position = "absolute";
            ring.style.left = "50%";
            ring.style.top = "50%";
            ring.style.transform = "translate(-50%, -50%)";
            ring.style.width = "44px";
            ring.style.height = "44px";
            ring.style.borderRadius = "9999px";
            ring.style.border = "2px solid #fbbf24";
            ring.style.boxShadow = "0 0 12px rgba(251,191,36,0.7)";
            ring.style.pointerEvents = "none";
            wrap.appendChild(ring);
          }
          const el = document.createElement("div");
          el.style.position = "absolute";
          el.style.left = "50%";
          el.style.top = "50%";
          el.style.transform = "translate(-50%, -50%)";
          el.style.width = p.is_critical ? "18px" : "14px";
          el.style.height = p.is_critical ? "18px" : "14px";
          el.style.borderRadius = "9999px";
          el.style.background = color;
          el.style.boxShadow = `0 0 10px ${color}, 0 0 18px ${color}55`;
          el.style.border = p.is_critical ? "2px solid #fda4af" : "1.5px solid #ffffff";
          el.style.cursor = "pointer";
          if (p.is_critical) {
            el.style.animation = "cc-pulse 1.6s ease-in-out infinite";
          }
          wrap.appendChild(el);

          // Hover popup gives a quick read without forcing navigation —
          // matches the Carte tab pattern.
          const label = EMERGENCY_FR[p.emergency_type]?.label || p.emergency_type;
          const popup = new maplibregl.Popup({
            offset: 14, closeButton: false, closeOnClick: true,
            className: "cc-marker-popup",
          }).setHTML(`
            <div style="font-family:ui-monospace,monospace;padding:2px 4px;min-width:160px;">
              <div style="font-weight:700;color:${color};font-size:12px;text-transform:uppercase;letter-spacing:0.15em;">${label}</div>
              <div style="font-size:10px;color:#a1a1aa;margin-top:2px;">
                ${p.is_unassigned ? "Non-assignée" : "Dispatchée"}${p.is_critical ? " · Critique" : ""}
              </div>
              <div style="font-size:10px;color:#71717a;margin-top:4px;">Clic pour ouvrir le dossier</div>
            </div>`);

          const marker = new maplibregl.Marker({ element: wrap })
            .setLngLat([p.coordinates.longitude, p.coordinates.latitude])
            .setPopup(popup)
            .addTo(map);
          wrap.addEventListener("click", (ev) => {
            ev.stopPropagation();
            navigate(`/admin/alerts#${p.id}`);
          });
          markersRef.current.push(marker);
        });
      }
    });
  }, [data, mapMode, styleVersion, newIds, navigate]);

  const resetView = () => {
    const map = mapRef.current;
    if (!map) return;
    map.flyTo({ center: MALI_VIEW.center, zoom: MALI_VIEW.zoom, speed: 1.4, essential: true });
  };

  // Fullscreen toggle. Tries the native browser Fullscreen API first
  // (so F11/escape behave naturally + the chrome-less view is genuinely
  // immersive). Falls back to a fixed-positioned overlay if the browser
  // refuses (e.g. iframes without `allowfullscreen`), so the operator
  // still gets an expanded view even on locked-down devices.
  const toggleFullscreen = useCallback(async () => {
    const el = mapCardRef.current;
    if (!el) return;
    const isNativeFs = document.fullscreenElement === el;
    try {
      if (isNativeFs) {
        await document.exitFullscreen();
      } else if (el.requestFullscreen) {
        await el.requestFullscreen({ navigationUI: "hide" });
      } else {
        setIsFullscreen((v) => !v);  // CSS-only fallback
      }
    } catch {
      setIsFullscreen((v) => !v);
    }
    // Allow MapLibre to recompute its size against the new bounds.
    setTimeout(() => { try { mapRef.current?.resize(); } catch { /* noop */ } }, 120);
  }, []);

  // Mirror the native fullscreenchange event into our state so the
  // toolbar icon flips even when the user hits Esc.
  useEffect(() => {
    const onFs = () => setIsFullscreen(document.fullscreenElement === mapCardRef.current);
    document.addEventListener("fullscreenchange", onFs);
    return () => document.removeEventListener("fullscreenchange", onFs);
  }, []);

  // ── Strip metrics ─────────────────────────────────────────────────
  const m = data?.metrics;
  const oldest = m?.oldest_pending;

  const titleByWindow = useMemo(() => {
    const opt = WINDOW_OPTIONS.find((o) => o.value === timeWindow);
    if (timeWindow === "24h") return "Alertes actives · Dernières 24h";
    if (timeWindow === "today") return "Alertes actives · Aujourd'hui";
    if (timeWindow === "7d") return "Alertes actives · 7 derniers jours";
    if (timeWindow === "30d") return "Alertes actives · 30 derniers jours";
    return `Alertes actives · ${opt?.label || timeWindow}`;
  }, [timeWindow]);

  return (
    <div className="px-3 sm:px-5 py-4 pb-8 space-y-4" data-testid="command-center-tab">
      {/* ── Header + time filters ────────────────────────────────── */}
      <div className="flex flex-wrap items-center gap-2 border-b border-zinc-800 pb-2">
        <Target className="w-4 h-4 text-emerald-400" strokeWidth={2.4} />
        <span className="text-[12px] font-semibold uppercase tracking-[0.16em] text-zinc-100">
          Centre de commandement
        </span>
        <span
          data-testid="cc-live-indicator"
          title={live ? "Connexion temps réel active" : "Hors-ligne — actualisation toutes les 30 s"}
          className={`inline-flex items-center gap-1 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-[0.12em] border rounded ${
            live
              ? "border-emerald-500/50 text-emerald-300 bg-emerald-500/10"
              : "border-amber-500/40 text-amber-300 bg-amber-500/5"
          }`}
        >
          {live ? <RadioTower className="w-3 h-3" /> : <WifiOff className="w-3 h-3" />}
          {live ? "Direct" : "Hors-ligne"}
        </span>
        {m && (
          <span className="font-mono text-[10px] text-zinc-500 ml-2">
            · {data.generated_at && new Date(data.generated_at).toLocaleTimeString("fr-FR")}
          </span>
        )}
        <div className="ml-auto" data-testid="cc-window-filters">
          {/* Iter80 — shared TimeFilterBar replaces the legacy 4-button
              chip row. Adds Dernière heure / Hier / 90 J / custom. */}
          <TimeFilterBar
            preset={timeWindow}
            customSince={tfSince}
            customUntil={tfUntil}
            onChange={({ preset: p, customSince: s, customUntil: u }) => {
              setTimeWindow(p);
              setTfSince(s ?? "");
              setTfUntil(u ?? "");
            }}
            short
            testidPrefix="cc-window"
          />
        </div>
        <button
          type="button"
          onClick={() => fetchSummary({ silent: true })}
          data-testid="cc-refresh"
          className="px-2 py-1 text-[10px] font-bold uppercase tracking-widest border border-zinc-700 text-zinc-300 hover:text-emerald-300 hover:border-emerald-500/50 rounded flex items-center gap-1.5"
          aria-label="Actualiser"
        >
          {refreshing ? <Loader2 className="w-3 h-3 animate-spin" /> : <RotateCw className="w-3 h-3" />}
          Actualiser
        </button>
      </div>

      {/* ── Live status strip ────────────────────────────────────── */}
      <div
        className="flex items-stretch gap-2 overflow-x-auto cmd-scroll-row pb-1"
        data-testid="cc-status-strip"
      >
        {loading || !m ? (
          <div className="text-xs text-zinc-500 font-mono uppercase tracking-widest py-3 px-2">
            Chargement de la situation…
          </div>
        ) : (
          <>
            <StripCell
              label="Actives"
              value={m.active}
              tone={m.active > 0 ? "info" : "neutral"}
              testid="cc-strip-active"
              key={`active-${activePulseKey}`}
              pulseClass={activePulseKey > 0 ? "cc-strip-amber-pulse" : ""}
            />
            <StripCell
              label="Non-assignées"
              value={m.unassigned}
              tone={m.unassigned > 0 ? "warn" : "success"}
              testid="cc-strip-unassigned"
              key={`unassigned-${unassignedPulseKey}`}
              pulseClass={unassignedPulseKey > 0 ? "cc-strip-amber-pulse" : ""}
            />
            <StripCell
              label="En attente >30 min"
              value={m.pending_over_30}
              tone={m.pending_over_30 > 0 ? "danger" : "success"}
              testid="cc-strip-overdue"
            />
            <StripCell
              label="Critiques"
              value={m.critical}
              tone={m.critical > 0 ? "danger" : "success"}
              sub="Sensibles ou en retard"
              testid="cc-strip-critical"
              key={`critical-${criticalPulseKey}`}
              pulseClass={criticalPulseKey > 0 ? "cc-strip-red-pulse" : ""}
            />
            <StripCell
              label="Plus ancienne"
              value={oldest ? fmtElapsed(oldest.elapsed_minutes) : "—"}
              tone={oldest && (oldest.elapsed_minutes || 0) > 30 ? "danger" : "neutral"}
              sub={oldest ? (EMERGENCY_FR[oldest.emergency_type]?.label || oldest.emergency_type) : "Aucune en attente"}
              testid="cc-strip-oldest"
            />
            <StripCell
              label="Délai médian dispatch"
              value={m.median_dispatch_delay_min != null ? `${m.median_dispatch_delay_min} min` : "—"}
              tone={
                m.median_dispatch_delay_min == null
                  ? "neutral"
                  : m.median_dispatch_delay_min > 15
                    ? "warn"
                    : "success"
              }
              sub="Création → 1er dispatch"
              testid="cc-strip-median"
            />
            <StripCell
              label="Résolues aujourd'hui"
              value={m.resolved_today}
              tone={m.resolved_today > 0 ? "success" : "neutral"}
              testid="cc-strip-resolved"
            />
          </>
        )}
      </div>

      {/* ── Two-column operational zone ──────────────────────────── */}
      <div className={`grid grid-cols-1 ${isFullscreen ? "" : "lg:grid-cols-[1fr_360px]"} gap-4`}>
        {/* Map */}
        <div
          ref={mapCardRef}
          className={`border border-zinc-800 bg-[#0b0f14] rounded relative overflow-hidden ${
            isFullscreen ? "fixed inset-0 z-[1000] rounded-none" : ""
          }`}
          data-testid="cc-map-card"
        >
          <div className="flex flex-wrap items-center gap-y-1 gap-x-2 px-3 py-2 border-b border-zinc-800">
            <Crosshair className="w-3.5 h-3.5 text-emerald-400" />
            <div className="text-[11px] font-bold uppercase tracking-[0.22em] text-zinc-200" data-testid="cc-map-title">
              {titleByWindow}
            </div>
            <div className="ml-auto flex flex-wrap items-center gap-1">
              <button
                type="button"
                onClick={() => setMapMode("markers")}
                data-testid="cc-map-mode-markers"
                className={`px-2 py-1 text-[10px] font-bold uppercase tracking-widest border rounded ${
                  mapMode === "markers"
                    ? "border-emerald-500/60 text-emerald-300 bg-emerald-500/10"
                    : "border-zinc-700 text-zinc-400"
                }`}
              >
                Marqueurs
              </button>
              <button
                type="button"
                onClick={() => setMapMode("heatmap")}
                data-testid="cc-map-mode-heatmap"
                className={`px-2 py-1 text-[10px] font-bold uppercase tracking-widest border rounded ${
                  mapMode === "heatmap"
                    ? "border-emerald-500/60 text-emerald-300 bg-emerald-500/10"
                    : "border-zinc-700 text-zinc-400"
                }`}
              >
                Heatmap
              </button>
              <button
                type="button"
                onClick={resetView}
                data-testid="cc-reset-view"
                className="ml-1 px-2 py-1 text-[10px] font-bold uppercase tracking-widest border border-zinc-700 text-zinc-300 hover:text-emerald-300 hover:border-emerald-500/50 rounded flex items-center gap-1"
                title={`Recentrer sur ${_ccName === "Mali" ? "le Mali" : "la " + _ccName}`}
              >
                <Target className="w-3 h-3" /> Vue nationale
              </button>
              {/* Layer switcher — parity with Carte tab */}
              <div className="ml-1 flex items-center border border-zinc-700 rounded overflow-hidden" data-testid="cc-layer-switch">
                {[
                  { value: "vector",  label: "Plan" },
                  { value: "hybrid",  label: "Satellite" },
                  { value: "dark",    label: "Sombre" },
                ].map((l) => (
                  <button
                    key={l.value}
                    type="button"
                    onClick={() => setMapLayer(l.value)}
                    data-testid={`cc-map-layer-${l.value}`}
                    className={`px-2 py-1 text-[10px] font-bold uppercase tracking-widest ${
                      mapLayer === l.value
                        ? "text-emerald-300 bg-emerald-500/10"
                        : "text-zinc-400 hover:text-zinc-200"
                    }`}
                    title={`Vue ${l.label.toLowerCase()}`}
                  >
                    {l.label}
                  </button>
                ))}
              </div>
              <button
                type="button"
                onClick={toggleFullscreen}
                data-testid="cc-fullscreen-toggle"
                aria-label={isFullscreen ? "Quitter le plein écran" : "Plein écran"}
                title={isFullscreen ? "Quitter le plein écran" : "Plein écran"}
                className="ml-1 px-2 py-1 text-[10px] font-bold uppercase tracking-widest border border-zinc-700 text-zinc-300 hover:text-emerald-300 hover:border-emerald-500/50 rounded flex items-center gap-1"
              >
                {isFullscreen ? <Minimize2 className="w-3 h-3" /> : <Maximize2 className="w-3 h-3" />}
                {isFullscreen ? "Réduire" : "Plein écran"}
              </button>
            </div>
          </div>
          <div
            ref={mapContainerRef}
            className={`w-full bg-zinc-950 ${isFullscreen ? "h-[calc(100vh-44px)]" : "h-[420px] sm:h-[540px]"}`}
            data-testid="cc-map-container"
          />
        </div>

        {/* Active alert queue */}
        <div
          className={`border border-zinc-800 bg-[#0b0f14] rounded flex flex-col ${isFullscreen ? "hidden" : ""}`}
          data-testid="cc-queue-card"
        >
          <div className="flex items-center gap-2 px-3 py-2 border-b border-zinc-800">
            <Siren className="w-3.5 h-3.5 text-rose-400" />
            <div className="text-[11px] font-bold uppercase tracking-[0.22em] text-zinc-200">
              File active
            </div>
            <span className="ml-1 text-[10px] text-zinc-500 font-mono">
              triée par urgence
            </span>
            {data?.queue && (
              <span className="ml-auto text-[10px] font-mono text-zinc-500">
                {data.queue.length}
              </span>
            )}
          </div>
          <div className="flex-1 overflow-y-auto px-2 py-2 space-y-1.5 max-h-[540px]" data-testid="cc-queue-list">
            {loading ? (
              <div className="text-xs text-zinc-500 font-mono py-4 text-center">Chargement…</div>
            ) : data?.queue?.length === 0 ? (
              <div className="text-xs text-zinc-500 font-mono py-4 text-center" data-testid="cc-queue-empty">
                Aucune alerte active sur cette période.
              </div>
            ) : (
              (data?.queue || []).map((a) => (
                <QueueRow
                  key={a.id}
                  a={a}
                  isNew={newIds.has(a.id)}
                  onClick={() => navigate(`/admin/alerts#${a.id}`)}
                />
              ))
            )}
          </div>
        </div>
      </div>

      {/* ── Historical analytics (collapsed by default) ──────────── */}
      <AnalyticsSummary defaultOpen={false} />
    </div>
  );
};

export default CommandCenterTab;
